import { USER_LOAN_ESTIMATION_TYPE } from '@/config/constants';
import { applicationHasCoBorrower } from '@/utils/loanApplicationBorrower';
import type {
  LoanApplication,
  LoanFlowProfile,
  LoanProductType,
} from '@/types/LoanApplicationType';

export type LoanSectionKey =
  | 'property'
  | 'borrower'
  | 'coBorrower'
  | 'assets'
  | 'track'
  | 'employment'
  | 'declarations'
  | 'loanTerms'
  | 'refinanceTerms'
  | 'investorProgram'
  | 'liabilities'
  | 'demographics'
  | 'dscr'
  | 'checklist'
  | 'sign';

export type LoanSectionDef = {
  key: LoanSectionKey;
  label: string;
  /** Co-borrower duplicate of borrower UI */
  coBorrower?: boolean;
};

const SHARED_SECTIONS: LoanSectionDef[] = [
  { key: 'property', label: 'Property information' },
  { key: 'borrower', label: 'Borrower information' },
  { key: 'coBorrower', label: 'Co-borrower', coBorrower: true },
  { key: 'assets', label: 'Asset information' },
  { key: 'track', label: 'Track record' },
  { key: 'employment', label: 'Employment' },
  { key: 'declarations', label: 'Declarations' },
];

/**
 * Residential (Conventional/FHA/VA/USDA) order: the home and the loan first —
 * what the borrower came to do — then about them, money in, money out, the
 * legal questions, documents and sign.
 */
const RESIDENTIAL_SECTIONS: LoanSectionDef[] = [
  { key: 'property', label: 'Property' },
  { key: 'refinanceTerms', label: 'Current mortgage' },
  { key: 'loanTerms', label: 'Your loan' },
  { key: 'borrower', label: 'About you' },
  { key: 'coBorrower', label: 'Co-borrower', coBorrower: true },
  { key: 'employment', label: 'Income & employment' },
  { key: 'assets', label: 'Assets' },
  { key: 'liabilities', label: 'Debts' },
  { key: 'declarations', label: 'Declarations' },
  { key: 'demographics', label: 'Demographics' },
  { key: 'checklist', label: 'Documents' },
  { key: 'sign', label: 'Review & sign' },
];

const INVESTOR_PROGRAM_EXTRA: LoanSectionDef[] = [
  { key: 'investorProgram', label: 'Program details' },
];

const DSCR_EXTRA: LoanSectionDef[] = [
  { key: 'dscr', label: 'DSCR & underwriting' },
  { key: 'checklist', label: 'Documents & conditions' },
  { key: 'sign', label: 'Attestation' },
];

/** Conventional, FHA, VA, USDA, and refi/other residential flows use URLA-aligned steps. */
export function usesUrlaStyleSections(
  productType: LoanProductType | string | undefined,
  flowProfile?: LoanFlowProfile | string
): boolean {
  if (productType === 'dscr' || productType === 'bridge') return false;
  if (
    productType === 'conventional' ||
    productType === 'fha' ||
    productType === 'va' ||
    productType === 'usda'
  ) {
    return true;
  }
  if (productType === 'other' && flowProfile === 'refinance') return true;
  if (productType === 'other' && flowProfile === 'purchase') return true;
  return false;
}

export function getSectionsForProduct(
  productType: LoanProductType | string | undefined,
  flowProfile?: LoanFlowProfile | string
): LoanSectionDef[] {
  const flow = flowProfile as LoanFlowProfile | undefined;
  const investorProgram =
    productType === 'bridge' || productType === 'other'
      ? INVESTOR_PROGRAM_EXTRA
      : [];

  if (productType === 'dscr') {
    return [...SHARED_SECTIONS, ...DSCR_EXTRA];
  }
  if (usesUrlaStyleSections(productType, flowProfile)) {
    return RESIDENTIAL_SECTIONS.filter(
      (s) => s.key !== 'refinanceTerms' || flow === 'refinance'
    );
  }
  return [
    ...SHARED_SECTIONS,
    ...investorProgram,
    { key: 'checklist', label: 'Documents' },
    { key: 'sign', label: 'Attestation' },
  ];
}

export type LoanSectionFlags = {
  showTrackRecord: boolean;
  showEntityQuestionPrimary: boolean;
  showDscr: boolean;
};

/** Viewer context for program-specific nav (track record is investor + DSCR/bridge). */
export type LoanSectionViewerContext = {
  loanEstimationType?: string | null;
  /** Admins previewing another user’s file — gate on application program only. */
  bypassInvestorGate?: boolean;
  /**
   * Standalone `/loan/:id` hub — property section only for refinance (purchase
   * property details live in the deal room per listing).
   */
  standaloneShell?: boolean;
  /**
   * When true, purchase master files still show Property information (e.g. opened
   * from a property card with `?focus=` or a linked subject listing).
   */
  showPurchasePropertySection?: boolean;
};

type AppSectionInput = Pick<
  LoanApplication,
  'flowProfile' | 'productType' | 'borrowers'
>;

function shouldShowTrackRecord(
  isInvestmentLoanProgram: boolean,
  viewer?: LoanSectionViewerContext | null
): boolean {
  if (!isInvestmentLoanProgram) return false;
  if (viewer == null) return true;
  if (viewer.bypassInvestorGate) return true;
  return viewer.loanEstimationType === USER_LOAN_ESTIMATION_TYPE.INVESTOR;
}

export function getSectionFlags(
  app: AppSectionInput,
  viewer?: LoanSectionViewerContext | null
): LoanSectionFlags {
  const flow = app.flowProfile as LoanFlowProfile | undefined;
  const isRefi = flow === 'refinance';
  const isInvestmentLoanProgram =
    app.productType === 'dscr' || app.productType === 'bridge';

  return {
    showTrackRecord: shouldShowTrackRecord(isInvestmentLoanProgram, viewer),
    // Entity / trust / partnership borrowers only make sense for investor
    // and commercial loans, not a personal purchase or refinance.
    showEntityQuestionPrimary:
      flow === 'investor_dscr' ||
      String(flow) === 'commercial' ||
      isInvestmentLoanProgram ||
      (app.productType === 'other' && flow !== 'refinance'),
    showDscr: app.productType === 'dscr' && !isRefi,
  };
}

/** Sections shown in the shell for this application (flow-aware). */
export function getSectionsForApplication(
  app: AppSectionInput,
  viewer?: LoanSectionViewerContext | null
): LoanSectionDef[] {
  const flags = getSectionFlags(app, viewer);
  const base = getSectionsForProduct(app.productType, app.flowProfile);
  const isRefi = app.flowProfile === 'refinance';
  return base.filter((s) => {
    // Property stays visible on a standalone purchase file: it is where the
    // borrower picks (or types) the home they are buying.
    if (
      s.key === 'property' &&
      viewer?.standaloneShell &&
      !isRefi &&
      !viewer?.showPurchasePropertySection &&
      !usesUrlaStyleSections(app.productType, app.flowProfile)
    ) {
      return false;
    }
    if (s.key === 'track' && !flags.showTrackRecord) return false;
    if (s.key === 'dscr' && !flags.showDscr) return false;
    if (s.key === 'refinanceTerms' && !isRefi) return false;
    if (
      s.key === 'investorProgram' &&
      app.productType !== 'bridge' &&
      app.productType !== 'other'
    ) {
      return false;
    }
    if (s.key === 'coBorrower' && !applicationHasCoBorrower(app)) return false;
    return true;
  });
}
